import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '../cn';

export type ButtonVariant = 'primary' | 'dark' | 'light' | 'ghost' | 'danger' | 'good';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  full?: boolean;
}

const variantClass: Record<ButtonVariant, string> = {
  // Ink on flame, not white: 5.28:1 against 3.17:1. `brand.dark` is the
  // darkest hover that keeps the label at AA (4.62).
  primary: 'bg-brand text-brand-ink border-brand hover:bg-brand-dark shadow-button',
  dark: 'bg-ink text-white border-ink hover:bg-ink-2',
  light: 'bg-surface text-ink border-stroke hover:bg-surface-2',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-surface-2',
  danger: 'bg-danger text-white border-danger hover:brightness-110',
  // Deep sage ground: white labels clear 4.5:1 here where the bright
  // primitive (#2f8f63) only manages 4.02:1.
  good: 'bg-good-ink text-white border-good-ink hover:brightness-110',
};

// Harvest puts every button at a 16px radius; that softness is half the
// system's tactile signature. `sm` steps down to 14px only because 16 on a
// 32px-tall control reads as a lozenge rather than a button.
const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-10 px-4 text-sm gap-2 rounded-item',
  lg: 'h-11 px-5 text-sm gap-2.5 rounded-item',
};

const baseClass =
  'inline-flex min-w-fit select-none items-center justify-center whitespace-nowrap border font-sans font-semibold leading-none tracking-ui transition-colors [&_svg]:shrink-0 disabled:cursor-not-allowed disabled:opacity-50';

export function buttonClassName({
  variant = 'primary',
  size = 'md',
  full,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  className?: string;
}) {
  return cn(baseClass, variantClass[variant], sizeClass[size], full && 'w-full', className);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, iconRight, full, children, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={buttonClassName({ variant, size, full, className })}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
});
